/**
 * Loading Service
 *
 * Manages global loading state for the application.
 * Uses RxJS BehaviorSubject to provide reactive loading indicators.
 * Typically used by the LoadingInterceptor to show/hide loading spinners during HTTP requests.
 *
 * @example
 * ```typescript
 * // In a component template
 * <p-progressSpinner *ngIf="loadingService.loading$ | async"></p-progressSpinner>
 *
 * // In a component class
 * constructor(public loadingService: LoadingService) {}
 *
 * // Manually show loading
 * this.loadingService.show();
 *
 * // Hide loading
 * this.loadingService.hide();
 * ```
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  /**
   * Internal BehaviorSubject holding the loading state
   * Private to prevent direct mutation from outside this service
   */
  private loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Observable stream of the loading state
   *
   * Components should subscribe to this to show/hide loading indicators.
   * Emits true when loading is active, false otherwise.
   *
   * @example
   * ```typescript
   * // In template
   * <div *ngIf="loadingService.loading$ | async" class="loading-overlay">
   *   <p-progressSpinner></p-progressSpinner>
   * </div>
   *
   * // In component
   * this.loadingService.loading$.subscribe(isLoading => {
   *   console.log('Loading:', isLoading);
   * });
   * ```
   */
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();

  /**
   * Shows the loading indicator
   *
   * Sets the loading state to true, which will emit to all subscribers.
   * Typically called automatically by the LoadingInterceptor when an HTTP request starts.
   *
   * @example
   * ```typescript
   * this.loadingService.show();
   * ```
   */
  show(): void {
    this.loadingSubject.next(true);
  }

  /**
   * Hides the loading indicator
   *
   * Sets the loading state to false, which will emit to all subscribers.
   * Typically called automatically by the LoadingInterceptor when an HTTP request completes.
   *
   * @example
   * ```typescript
   * this.loadingService.hide();
   * ```
   */
  hide(): void {
    this.loadingSubject.next(false);
  }
}
